import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alan, BosDurum, Buton, cn, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import {
  ApiHatasi,
  yenilemeRiski,
  type RiskEtkeni,
  type RiskSatiri,
  type RiskSeviyesi,
} from "./api";

/**
 * YENILEME RISKI — F-ANL-10
 *
 * "Bu ogrenci paketini yenilemeyebilir" listesi. Akademik riskten
 * AYRI: akademik olarak zorlanan bir ogrenci ticari olarak saglam
 * olabilir, tersi de gecerli.
 */

const ETKEN_ADI: Record<RiskEtkeni, string> = {
  veriGirisi: "Veri girişi",
  gorusmeKatilimi: "Görüşme katılımı",
  veliIlgisi: "Veli ilgisi",
  odemeGecikmesi: "Ödeme",
  paketBitisi: "Paket bitişi",
};

const SEVIYE_ADI: Record<RiskSeviyesi, string> = {
  DUSUK: "Düşük",
  ORTA: "Orta",
  YUKSEK: "Yüksek",
};

const SEVIYE_SINIFI: Record<RiskSeviyesi, string> = {
  DUSUK: "bg-basarili-500/15 text-onay-700",
  ORTA: "bg-uyari-500/15 text-uyari-800",
  YUKSEK: "bg-hata-500/15 text-hata-700",
};

export function YenilemeRiski({ onOgrenciSec }: { onOgrenciSec?: (id: string) => void }) {
  const [ara, setAra] = useState("");

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["yenileme-riski"],
    queryFn: yenilemeRiski,
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Risk listesi alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.satirlar.length === 0) {
    return (
      <BosDurum
        baslik="Kapsamınızda aktif öğrenci yok"
        aciklama="Risk skoru yalnızca aktif öğrenciler için hesaplanır."
      />
    );
  }

  /**
   * ================================================================
   * OLCULEMEYENLER AYRI BOLUMDE
   * ================================================================
   * Ayni listede olsalardi ya en basta ya en sonda dururlardi ve
   * ikisi de yanlis: basta olsalar liste her donem basinda yeni
   * kayitlarla dolar, gercek riskliler asagi kayar; sonda olsalar
   * "bu ogrenciler hakkinda bilgimiz yok" mesaji kaybolur.
   *
   * Ayri bir bolum ikisini de cozuyor: risk listesi temiz kaliyor,
   * "henuz olculemiyor" bilgisi de gorunuyor.
   */
  /*
    ARAMA — doksan satirda tek tek bakmak yok. Suzgec ISTEMCIDE:
    liste zaten sunucudan `LISTE_SINIRI` ile geliyor ve her harfte
    yeni bir istek atmak gereksiz.
  */
  const sorgu = ara.trim().toLocaleLowerCase("tr");
  const uyar = (r: RiskSatiri) =>
    !sorgu || r.ad.toLocaleLowerCase("tr").includes(sorgu) || String(r.studentNo).includes(sorgu);

  const olculenler = data.satirlar.filter((s) => s.sonuc.olculdu && uyar(s));
  const olculemeyenler = data.satirlar.filter((s) => !s.sonuc.olculdu && uyar(s));
  const hicSonucYok = sorgu !== "" && olculenler.length === 0 && olculemeyenler.length === 0;

  /**
   * Olculemeyenlerin HEPSINDE ayni etkenler mi eksik.
   *
   * Ayniysa cumle bir kez yaziliyor; degilse `null` donuyor ve her
   * kart kendi listesini yaziyor. Tek satirda bile ozetlemek dogru:
   * "1 ogrenci" listesinde de cumle karta degil bolume ait.
   */
  const ortakEksik = ((): string | null => {
    if (olculemeyenler.length === 0) return null;
    const imza = (r: RiskSatiri): string => [...r.sonuc.olculemeyen].sort().join("|");
    const ilk = imza(olculemeyenler[0]!);
    if (!olculemeyenler.every((r) => imza(r) === ilk)) return null;
    return olculemeyenler[0]!.sonuc.olculemeyen.map((e) => ETKEN_ADI[e]).join(", ");
  })();

  return (
    <div className="flex flex-col gap-6">
      {/*
        Odeme etkeni disarida kaldiysa SOYLENIYOR. Soylenmezse uzman,
        yoneticinin gordugu skorla kendi gordugu arasindaki farki
        hataya yorar.
      */}
      {!data.odemeEtkeniDahil && (
        <p className="rounded-alan bg-notr-50 text-notr-600 p-3 text-xs">
          Bu skor <strong>ödeme bilgisi olmadan</strong> hesaplandı. Ödeme durumu yetkiniz dışında
          olduğu için etken hesaba katılmıyor — hizmet kalitesinin ödemeye bağlı olmaması için
          bilinçli bir kısıt.
        </p>
      )}

      <div className="max-w-sm">
        <Alan
          etiket="Ara"
          type="search"
          value={ara}
          onChange={(e) => setAra(e.target.value)}
          yardim="Öğrenci adı ya da numarası."
        />
      </div>

      {hicSonucYok && (
        <BosDurum
          baslik="Aramaya uyan öğrenci yok"
          aciklama={`“${ara.trim()}” için sonuç bulunamadı.`}
          eylem={
            <Buton gorunum="ikincil" onClick={() => setAra("")}>
              Aramayı temizle
            </Buton>
          }
        />
      )}

      {olculenler.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-notr-900 text-sm font-semibold">
            Risk sırasına göre ({olculenler.length})
          </h2>
          <ul className="flex flex-col gap-2">
            {olculenler.map((s) => (
              <li key={s.ogrenciId}>
                <RiskKarti satir={s} onSec={onOgrenciSec} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {olculemeyenler.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-notr-900 text-sm font-semibold">
            Henüz ölçülemiyor ({olculemeyenler.length})
          </h2>
          {/*
            "Risk yok" DEMEK DEGIL. Bu ayrimi yazmak sart: aksi halde
            liste "bu ogrenciler saglam" diye okunur ve dun kayit olmus
            bir ogrenci gozden kacar.
          */}
          <p className="text-notr-600 text-xs">
            Bu öğrenciler hakkında skor üretecek kadar veri yok.{" "}
            <strong>Risk yok demek değil</strong> — henüz bilinmiyor.
          </p>

          {/*
            ================================================================
            AYNI CUMLEYI 26 KEZ YAZMA
            ================================================================
            07.08.2026'da bulundu: 26 satirin 26'sinda "Skor üretilemedi —
            ölçülemeyen: ..." yaziyordu. Davranis DOGRUYDU (olculmedi ≠
            sifir) ama ekran okunmaz haldeydi: tekrar eden bir cumle
            bilgi tasimiyor, yalnizca listeyi uzatiyor.

            Eksik etkenler HERKESTE AYNIYSA bir kez yaziliyor ve
            kartlardan dusuyor. Farkli olanlar varsa kart kendi
            listesini yaziyor — ozetleyip farki gizlemek, "hepsi ayni
            sebeple olculemiyor" gibi YANLIS bir izlenim verirdi.
          */}
          {ortakEksik !== null ? (
            <p className="text-notr-600 text-xs">
              Hepsinde eksik olan: <strong>{ortakEksik}</strong>
            </p>
          ) : null}

          <ul className="flex flex-col gap-2">
            {olculemeyenler.map((s) => (
              <li key={s.ogrenciId}>
                <RiskKarti satir={s} onSec={onOgrenciSec} eksikGizli={ortakEksik !== null} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function RiskKarti({
  satir,
  onSec,
  eksikGizli = false,
}: {
  satir: RiskSatiri;
  onSec?: (id: string) => void;
  /** Eksik etken listesi BOLUM basliginda yazildi — kartta tekrarlama */
  eksikGizli?: boolean;
}) {
  const govde = (
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        {/*
          ================================================================
          AD, NUMARA DEĞİL — 07.09.2026
          ================================================================
          Burada "Öğrenci no 176" yazıyordu. Doksan riskli öğrenciyi
          numarayla okuyan kimse yok; kullanıcı numarayı bir ada
          çevirmek zorunda kalıyor ve bunu yapabileceği bir yer de yok.
          Numara ikincil satırda kalıyor: bazı kurumlarda öğrenciye
          numarasıyla hitap ediliyor ve arama da numarayla yapılıyor.
        */}
        {onSec ? (
          /*
            ================================================================
            ADIN KENDISI DUGME, KART DEGIL — UI/UX Dalga 1 (axe G6, M-12)
            ================================================================
            Eskiden butun kart bir <button>'du ve "Nasil hesaplaniyor?"
            <details>'i onun ICINDEYDI: dugme icinde etkilesimli oge gecersiz,
            ekran okuyucu ozeti ayri bir denetim olarak duyuramiyor ve ozete
            tiklamak ayni anda ogrenciyi de aciyordu (hesabi okumak isteyen
            uzman profile firlatiliyordu).
            Kartin tamami yine tiklanir: dugmenin ::after'i karti kapliyor;
            details ustte (z-10) oldugu icin kendi tiklamasini aliyor.
          */
          <button
            type="button"
            onClick={() => onSec(satir.ogrenciId)}
            className={cn(
              "text-notr-900 text-left text-sm font-semibold focus-visible:outline-none",
              "after:rounded-kart after:absolute after:inset-0 after:content-['']",
              "focus-visible:after:outline-marka-600 focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2",
            )}
          >
            {satir.ad}
          </button>
        ) : (
          <p className="text-notr-900 text-sm font-semibold">{satir.ad}</p>
        )}
        <p className="text-notr-600 text-xs">No {satir.studentNo}</p>

        {satir.sonuc.olculdu ? (
          <>
            {/*
              Skor tek basina eyleme donusmuyor: "%68 riskli" uzmana
              ne yapacagini soylemiyor, "veli raporlari acmiyor"
              soyluyor.
            */}
            {satir.baskinEtkenAciklamasi && (
              <p className="text-notr-700 mt-0.5 text-sm">{satir.baskinEtkenAciklamasi}</p>
            )}
            {satir.sonuc.olculemeyen.length > 0 && (
              <p className="text-notr-600 mt-1 text-xs">
                Ölçülemeyen: {satir.sonuc.olculemeyen.map((e) => ETKEN_ADI[e]).join(", ")}
              </p>
            )}

            {/*
              ================================================================
              "NASIL HESAPLANIYOR?" — F-CRM-10
              ================================================================
              Ekran bir sayı gösteriyordu ama sayının NEREDEN geldiğini
              göstermiyordu. Açıklanamayan skor iki yönde de zarar
              veriyor: güvenilmezse hiç kullanılmıyor, körü körüne
              güvenilirse ölçülemeyen etkenler görünmez kalıyor.

              Veri sunucudan ZATEN geliyordu (`katkilar`: etken · risk ·
              ağırlık); eksik olan yalnızca sunumdu.

              KATLANIR BİR AYRINTI, VARSAYILAN KAPALI: listede yirmi
              öğrenci var ve uzman önce kimin riskli olduğuna bakıyor.
              Hesabı sürekli açık tutmak, asıl sıralamayı ayrıntının
              altına gömerdi.

              Etkenler AĞIRLIĞA göre sıralanıyor: skoru en çok hangi
              etkenin taşıdığı listenin başında okunmalı.
            */}
            <details className="group relative z-10 mt-2">
              <summary className="text-marka-700 hover:text-marka-600 cursor-pointer text-xs font-medium">
                Nasıl hesaplanıyor?
              </summary>
              <ul className="mt-1.5 flex flex-col gap-1">
                {[...satir.sonuc.katkilar]
                  .sort((a, b) => b.agirlik - a.agirlik)
                  .map((k) => (
                    <li key={k.etken} className="flex items-baseline justify-between gap-3 text-xs">
                      <span className="text-notr-700">{ETKEN_ADI[k.etken]}</span>
                      <span className="text-notr-600 shrink-0 tabular-nums">
                        risk {k.risk} · ağırlık %{Math.round(k.agirlik * 100)}
                      </span>
                    </li>
                  ))}
              </ul>
              {satir.sonuc.olculemeyen.length > 0 && (
                /*
                  ÖLÇÜLEMEYENLER HESABIN İÇİNDE DE TEKRARLANIYOR.
                  Yukarıda bir kez yazılıyor ama orası "bu öğrenci
                  hakkında ne bilmiyoruz" satırı; burası "bu skor neyi
                  hesaba katmadı" sorusunun cevabı. Ölçülemeyeni
                  hesabın dışında bırakmak, skoru olduğundan güvenilir
                  gösterirdi — çizimin kendi uyarısı
                  (`YenilemeRiski.dc.html`) tam olarak bu.
                */
                <p className="text-uyari-600 mt-1.5 text-xs">
                  Bu etkenler hesaba KATILMADI:{" "}
                  {satir.sonuc.olculemeyen.map((e) => ETKEN_ADI[e]).join(", ")}
                </p>
              )}
            </details>
          </>
        ) : eksikGizli ? null : (
          <p className="text-notr-600 mt-0.5 text-sm">
            Ölçülemeyen: {satir.sonuc.olculemeyen.map((e) => ETKEN_ADI[e]).join(", ")}
          </p>
        )}
      </div>

      {satir.sonuc.olculdu ? (
        <span
          className={cn(
            "rounded-alan shrink-0 px-2.5 py-0.5 text-xs font-medium tabular-nums",
            SEVIYE_SINIFI[satir.sonuc.seviye],
          )}
        >
          {SEVIYE_ADI[satir.sonuc.seviye]} · {satir.sonuc.skor}
        </span>
      ) : (
        /*
          "—" gosteriliyor, "0" DEGIL. Sifir gostermek, hakkinda
          bilgi olmayan ogrenciyi en saglam ogrenci gibi gosterirdi.
        */
        <Rozet className="shrink-0">—</Rozet>
      )}
    </div>
  );

  if (!onSec) {
    return <Kart>{govde}</Kart>;
  }

  return (
    <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart hover:bg-notr-50 relative w-full border p-4 text-left">
      {govde}
    </div>
  );
}
